import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Send,
  ArrowDownLeft,
  Target,
  UserCheck,
  TrendingUp,
  Zap,
  Award,
  Sparkles,
  Gift,
  Tag,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
  MoreVertical,
  Users,
  Check,
  ArrowRight,
  Crown,
  X,
  Star,
  CheckCircle,
  CreditCard,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getUserSubscription, getPlans } from "../apis/subscription";
import { getDashboardAnalytics } from "../apis/dashboard";
import SubscriptionModal from "../components/SubscriptionModal";
import BusinessDetailsStrip from "../components/BusinessDetailsStrip";
import PlansBlock from "../components/PlansBlock";
import PopularPlanOffer from "../components/PopularPlanOffer";
import ExploreAIChatbot from "../components/ExploreAIChatbot";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const Dashboard = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showExploreChatbot, setShowExploreChatbot] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const navigate = useNavigate();

  // Load subscription data and analytics
  useEffect(() => {
    loadSubscriptionData();
    loadAnalyticsData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setIsLoadingSubscription(true);
      const [subscriptionResponse, plansResponse] = await Promise.all([
        getUserSubscription(),
        getPlans(),
      ]);

      if (subscriptionResponse.data.success && subscriptionResponse.data.data) {
        const subscription = subscriptionResponse.data.data;
        const expiresAt = new Date(subscription.expires_at);
        const isExpired = expiresAt < new Date();
        
        if (!isExpired) {
          setCurrentSubscription(subscription);
        } else {
          setCurrentSubscription(null);
        }
      } else {
        setCurrentSubscription(null);
      }

      if (plansResponse.data.success) {
        const allPlans = plansResponse.data.data;
        const hasUsedTrialValue = plansResponse.data.has_used_trial || false;
        setHasUsedTrial(hasUsedTrialValue);
        const trial = allPlans.find(p => p.plan_name === 'trial');
        setTrialPlan(trial);
      }
    } catch (error) {
      console.error("Error loading subscription data:", error);
      setCurrentSubscription(null);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handleSubscriptionSuccess = () => {
    loadSubscriptionData();
    setShowPlansModal(false);
  };

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      setIsLoadingAnalytics(true);
      const response = await getDashboardAnalytics(7);
      if (response.data.success && response.data.data) {
        setAnalyticsData(response.data.data);
      }
    } catch (error) {
      console.error("Error loading analytics data:", error);
      // Set default empty data on error
      setAnalyticsData({
        statistics: {
          total_messages: 0,
          sent_count: 0,
          failed_count: 0,
          total_incoming_messages: 0,
          success_rate: 0,
        },
        day_wise: [],
        campaigns: {
          total: 0,
          active: 0,
          completed: 0,
        },
        broadcast: {
          total_sent: 0,
        },
        engagement: {
          unique_users: 0,
          total_replies: 0,
          avg_score: 0,
        },
      });
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // Campaign Performance Data - calculated from analytics
  const campaignStats = analyticsData ? [
    {
      name: "Broadcast Sent",
      current: analyticsData.broadcast?.total_sent || 0,
      total: Math.max(analyticsData.broadcast?.total_sent || 0, 1000),
      percentage: analyticsData.broadcast?.total_sent > 0 
        ? Math.min(Math.round((analyticsData.broadcast.total_sent / Math.max(analyticsData.broadcast.total_sent, 1000)) * 100), 100)
        : 0,
      unit: "",
      color: "#eefa94",
    },
    {
      name: "Active Campaigns",
      current: analyticsData.campaigns?.active || 0,
      total: Math.max(analyticsData.campaigns?.total || 0, 100),
      percentage: analyticsData.campaigns?.total > 0
        ? Math.round((analyticsData.campaigns.active / analyticsData.campaigns.total) * 100)
        : 0,
      unit: "",
      color: "#e9eeea",
    },
  ] : [
    {
      name: "Broadcast Sent",
      current: 0,
      total: 1000,
      percentage: 0,
      unit: "",
      color: "#eefa94",
    },
    {
      name: "Active Campaigns",
      current: 0,
      total: 100,
      percentage: 0,
      unit: "",
      color: "#e9eeea",
    },
  ];

  // Statistics Chart Data - campaigns vs broadcast (normalized to 0-1 scale)
  const tripsData = analyticsData?.day_wise?.map((day, index) => {
    const maxValue = Math.max(
      ...(analyticsData.day_wise.map(d => Math.max(d.sent_count, d.received_count))),
      1
    );
    return {
      month: day.date,
      operations: analyticsData.campaigns?.total > 0 ? Math.min(analyticsData.campaigns.active / Math.max(analyticsData.campaigns.total, 1), 1) : 0,
      transfer: maxValue > 0 ? Math.min(day.sent_count / maxValue, 1) : 0,
    };
  }) || [];

  // Message Volume Data - inbound and outbound separately
  const messageVolumeData = analyticsData?.day_wise?.map((d) => ({
    name: d.date,
    inbound: d.received_count || 0,
    outbound: d.sent_count || 0,
  })) || [];

  // Engagement data from engagement_scores table
  const engagementVolumeData = analyticsData?.engagement?.day_wise?.map((d) => ({
    name: d.date,
    unique_users: parseInt(d.unique_users || 0),
    avg_score: parseFloat(d.avg_score || 0),
  })) || [];

  const messageStats = analyticsData ? [
    {
      label: "Total Messages",
      value: (analyticsData.statistics?.total_messages || 0).toLocaleString(),
      change: "+0%",
      isPositive: true,
    },
    {
      label: "Messages Sent",
      value: (analyticsData.statistics?.sent_count || 0).toLocaleString(),
      change: "+0%",
      isPositive: true,
    },
    {
      label: "Messages Received",
      value: (analyticsData.statistics?.total_incoming_messages || 0).toLocaleString(),
      change: "+0%",
      isPositive: true,
    },
    {
      label: "Failed Count",
      value: (analyticsData.statistics?.failed_count || 0).toLocaleString(),
      change: "+0%",
      isPositive: false,
    },
    {
      label: "Success Rate",
      value: (analyticsData.statistics?.success_rate || 0) + "%",
      change: "+0%",
      isPositive: true,
    },
  ] : [];

  // Render pill indicators
  const renderPillIndicators = (total, filled) => {
    const pills = [];
    for (let i = 0; i < total; i++) {
      pills.push(
        <div
          key={i}
          className={`h-8 rounded-full transition-all ${
            i < filled ? "bg-gray-900 w-12" : "bg-gray-300 w-12 opacity-40"
          }`}
        />
      );
    }
    return pills;
  };

  // Custom bar shape component for rounded bars
  const RoundedBar = (props) => {
    const { fill, x, y, width, height } = props;
    const radius = 20;

    if (height <= 0) return null;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={radius}
          ry={radius}
        />
        <circle cx={x + width / 2} cy={y} r={4} fill="white" />
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-[#ffff]">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Business Details Strip */}
          <BusinessDetailsStrip />

          {/* AI Explore Blocks and Popular Plan Offer */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* AI WhatsApp Explore */}
            <div className="h-full">
              <div
                className="rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer h-full flex flex-col"
                style={{ backgroundColor: "#e9eeea" }}
                onClick={() => setShowExploreChatbot(true)}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Icon */}
                    <div className="p-3 bg-[#17181b]/90 rounded-xl flex-shrink-0 shadow-md">
                      <Sparkles className="text-white" size={24} />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 mb-2">
                        AI WhatsApp Explore
                      </h3>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Experience our AI-powered WhatsApp chatbot. Try it out with a demo conversation and see how intelligent automation can transform customer engagement.
                      </p>
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-[#17181b]/90 text-white rounded-xl font-semibold text-sm hover:bg-[#17181b]/80 transition-all shadow-md hover:shadow-lg whitespace-nowrap mt-auto w-fit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExploreChatbot(true);
                    }}
                  >
                    Explore
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* AI Template Explorer */}
            <div className="h-full">
              <div
                className="rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer h-full flex flex-col"
                style={{ backgroundColor: "#e9eeea" }}
                onClick={() => navigate("/templates/create")}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Icon */}
                    <div className="p-3 bg-[#17181b]/90 rounded-xl flex-shrink-0 shadow-md">
                      <FileText className="text-white" size={24} />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 mb-2">
                        AI Template Explorer
                      </h3>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Design and create message templates with AI assistance. Generate professional templates that get approved faster and engage better.
                      </p>
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-[#17181b]/90 text-white rounded-xl font-semibold text-sm hover:bg-[#17181b]/80 transition-all shadow-md hover:shadow-lg whitespace-nowrap mt-auto w-fit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/templates/create");
                    }}
                  >
                    Explore
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Popular Plan Offer */}
            <div className="h-full">
              <PopularPlanOffer onPlanClick={() => setShowPlansModal(true)} />
            </div>
          </div>

          {/* Stats Grid - Reduced Size */}
        {isLoadingAnalytics ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="rounded-2xl p-4 border border-gray-200 animate-pulse"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-16 mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-20"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {messageStats.map((stat, index) => (
            <div
              key={index}
              className="rounded-2xl p-4 border border-gray-200 hover:shadow-sm transition-shadow"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <p className="text-xs text-gray-600 mb-1.5 font-medium">
                {stat.label}
              </p>
              <p className="text-xl font-bold text-gray-900 mb-2">
                {stat.value}
              </p>
              <div className="flex items-center gap-1">
                {stat.isPositive ? (
                  <ArrowUpRight className="text-emerald-600" size={12} />
                ) : (
                  <ArrowDownRight className="text-rose-600" size={12} />
                )}
                <span
                  className={`text-xs font-semibold ${
                    stat.isPositive ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {stat.change}
                </span>
                <span className="text-xs text-gray-500">vs last week</span>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Campaign Performance & Upgrade Section - COMMENTED OUT */}
        {false && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          {/* Active Campaigns Card */}
          <div
            className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="p-2.5 bg-white rounded-xl">
                <Zap className="text-gray-700" size={20} />
              </div>
              <button className="p-1 hover:bg-white/50 rounded-lg transition-colors">
                <MoreVertical className="text-gray-600" size={18} />
              </button>
            </div>

            <h3 className="text-sm font-medium text-gray-600 mb-2">
              Active Campaigns
            </h3>

            {isLoadingAnalytics ? (
              <div className="animate-pulse">
                <div className="h-12 bg-gray-300 rounded w-32 mb-4"></div>
                <div className="h-6 bg-gray-300 rounded w-24 mb-6"></div>
                <div className="flex gap-2">{renderPillIndicators(7, 0)}</div>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-5xl font-bold text-gray-900">
                    {campaignStats[1]?.current || 0}
                  </span>
                  <span className="text-lg text-gray-500 mb-1.5">
                    / {campaignStats[1]?.total || 0}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/60 rounded-full">
                    <span className="text-xs font-semibold text-gray-700">
                      {campaignStats[1]?.percentage || 0}%
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">{renderPillIndicators(7, Math.min(Math.round((campaignStats[1]?.percentage || 0) / 15), 7))}</div>
              </>
            )}
          </div>

          {/* Broadcast Sent Card */}
          <div
            className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
            style={{ backgroundColor: "#eefa94" }}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="p-2.5 bg-white rounded-xl">
                <Send className="text-gray-700" size={20} />
              </div>
              <button className="p-1 hover:bg-white/50 rounded-lg transition-colors">
                <MoreVertical className="text-gray-600" size={18} />
              </button>
            </div>

            <h3 className="text-sm font-medium text-gray-600 mb-2">
              Broadcast Sent
            </h3>

            {isLoadingAnalytics ? (
              <div className="animate-pulse">
                <div className="h-12 bg-gray-300 rounded w-32 mb-4"></div>
                <div className="h-6 bg-gray-300 rounded w-24 mb-6"></div>
                <div className="flex gap-2">{renderPillIndicators(7, 0)}</div>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-5xl font-bold text-gray-900">
                    {campaignStats[0]?.current || 0}
                  </span>
                  <span className="text-lg text-gray-500 mb-1.5">
                    / {campaignStats[0]?.total || 0} {campaignStats[0]?.unit || ""}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/60 rounded-full">
                    <span className="text-xs font-semibold text-gray-700">
                      {campaignStats[0]?.percentage || 0}%
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">{renderPillIndicators(7, Math.min(Math.round((campaignStats[0]?.percentage || 0) / 15), 7))}</div>
              </>
            )}
          </div>

          {/* Subscription Card */}
          {!isLoadingSubscription && (
            <>
              {/* Trial Offer Card - Show if user hasn't used trial */}
              {!hasUsedTrial && trialPlan && !currentSubscription && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#dbeafe" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <Star className="text-blue-600" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      Limited Time
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Start with Trial
                  </h3>

                  <div className="mb-4">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-4xl font-bold text-gray-900">
                        ₹500
                      </span>
                      <span className="text-sm text-gray-600">/month</span>
                    </div>
                    <p className="text-xs text-gray-700 font-medium leading-relaxed">
                      Try all features for one month • Full access to everything
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    Get Started
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* Active Subscription Card - Show if user has active subscription */}
              {currentSubscription && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#e9eeea" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <Crown className="text-purple-600" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                      Active
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Current Plan
                  </h3>

                  <div className="mb-4">
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      {currentSubscription.plan_display_name}
                    </p>
                    <p className="text-xs text-gray-600 font-medium">
                      Expires: {new Date(currentSubscription.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    Upgrade / Renew
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* No Subscription Card - Show if no active subscription and trial used */}
              {!currentSubscription && hasUsedTrial && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#e9eeea" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <CreditCard className="text-gray-700" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                      Subscribe
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Subscription
                  </h3>

                  <div className="mb-4">
                    <p className="text-lg font-bold text-gray-900 mb-2">
                      No Active Plan
                    </p>
                    <p className="text-xs text-gray-600 font-medium">
                      Subscribe to unlock all features
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    View Plans
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        )}


        {/* Additional Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Message Volume Chart */}
          <div
            className="rounded-3xl p-6 border border-gray-200"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-white rounded-xl">
                <MessageSquare className="text-gray-700" size={18} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Message Volume Trend
              </h3>
            </div>
            <div className="h-[280px]">
              {isLoadingAnalytics || !messageVolumeData || messageVolumeData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Loading chart data...</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={messageVolumeData}>
                  <defs>
                    <linearGradient id="inboundFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1f2937" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1f2937" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outboundFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eefa94" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#eefa94" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#d1d5db"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="inbound"
                    stroke="#1f2937"
                    strokeWidth={2}
                    fill="url(#inboundFill)"
                    name="Inbound"
                  />
                  <Area
                    type="monotone"
                    dataKey="outbound"
                    stroke="#c5d930"
                    strokeWidth={2}
                    fill="url(#outboundFill)"
                    name="Outbound"
                  />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Engagement Chart */}
          <div
            className="rounded-3xl p-6 border border-gray-200"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-white rounded-xl">
                <UserCheck className="text-gray-700" size={18} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                User Engagement Metrics
              </h3>
            </div>
            <div className="h-[280px]">
              {isLoadingAnalytics || !engagementVolumeData || engagementVolumeData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Loading chart data...</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={engagementVolumeData}>
                  <defs>
                    <linearGradient id="engUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1f2937" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1f2937" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="engScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eefa94" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#eefa94" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#d1d5db"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="unique_users"
                    stroke="#1f2937"
                    fill="url(#engUsers)"
                    strokeWidth={2}
                    name="Unique Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="avg_score"
                    stroke="#c5d930"
                    fill="url(#engScore)"
                    strokeWidth={2}
                    name="Avg Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Plans Block */}
        {/* <PlansBlock onPlanClick={() => setShowPlansModal(true)} /> */}
        </div>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        onSuccess={handleSubscriptionSuccess}
        allowClose={true}
      />

      {/* Explore AI Chatbot Component */}
      <ExploreAIChatbot
        isOpen={showExploreChatbot}
        onClose={() => setShowExploreChatbot(false)}
      />
    </div>
  );
};

export default Dashboard;
